import {
  HStack,
  IconButton,
  Input,
  Spacer,
  Stack,
  Text,
} from "@chakra-ui/react";
import * as React from "react";
import { LuArchive, LuPencil } from "react-icons/lu";

import {
  CampaignCriteria,
  CampaignCriteriaKind,
  archiveCampaignCriteria,
  getCampaignCriteriaList,
  patchCampaignCriteria,
} from "../api/campaign-criteria";
import { useLoading } from "../hooks/loading";
import { useToaster } from "../hooks/toaster";
import { Button } from "./button";
import { ChoiceCard } from "./choice-card";
import { toaster } from "./toaster";

interface CampaignCriteriaPresetPickerProps {
  workspaceSlug: string;
  selectedId: number | null;
  onSelectionChange: (preset: CampaignCriteria | null) => void;
  emptyText: string;
}

// Shared by the turn-on wizard and the edit dialog, and owns the list so
// renaming and archiving can refresh it without either caller holding
// state it has no other use for.
export function CampaignCriteriaPresetPicker({
  emptyText,
  onSelectionChange,
  selectedId,
  workspaceSlug,
}: CampaignCriteriaPresetPickerProps) {
  // Null until the first load answers, so the empty state doesn't flash
  // on the way to a list that isn't empty.
  const [presets, setPresets] = React.useState<CampaignCriteria[] | null>(null);
  const [renamingId, setRenamingId] = React.useState<number | null>(null);
  const [renameValue, setRenameValue] = React.useState("");
  const [isWorking, withWorking] = useLoading();
  const withToaster = useToaster();

  const load = React.useCallback(async () => {
    await withToaster(async () => {
      setPresets(
        await getCampaignCriteriaList(workspaceSlug, {
          kind: CampaignCriteriaKind.PRESET,
        }),
      );
    });
  }, [withToaster, workspaceSlug]);

  React.useEffect(() => {
    load();
  }, [load]);

  const rename = async (preset: CampaignCriteria) => {
    await withWorking(async () => {
      await withToaster(async () => {
        await patchCampaignCriteria(workspaceSlug, preset.id, {
          name: renameValue.trim() || preset.name,
        });
        setRenamingId(null);
        await load();
      });
    });
  };

  // Archived rather than deleted: the ad groups already using this preset
  // keep it, and their terms hold the only handle on what they excluded.
  const archive = async (preset: CampaignCriteria) => {
    await withWorking(async () => {
      await withToaster(async () => {
        await archiveCampaignCriteria(workspaceSlug, preset.id);
        if (selectedId === preset.id) {
          onSelectionChange(null);
        }
        toaster.create({
          type: "success",
          title: `Archived "${preset.name}"`,
        });
        await load();
      });
    });
  };

  if (!presets) return null;

  if (presets.length === 0) {
    return (
      <Text color="fg.muted" fontSize="sm">
        {emptyText}
      </Text>
    );
  }

  return (
    <Stack gap="2">
      {presets.map((preset) => (
        <ChoiceCard
          key={preset.id}
          title={
            renamingId === preset.id ? (
              <HStack gap="2" onClick={(e) => e.stopPropagation()}>
                <Input
                  autoFocus
                  size="sm"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setRenamingId(null)}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="surface"
                  loading={isWorking}
                  onClick={() => rename(preset)}
                >
                  Save name
                </Button>
              </HStack>
            ) : (
              <HStack gap="2" width="full">
                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  truncate
                  title={preset.name}
                >
                  {preset.name}
                </Text>
                <Spacer />
                <HStack gap="1" onClick={(e) => e.stopPropagation()}>
                  <IconButton
                    aria-label="Rename preset"
                    size="xs"
                    variant="ghost"
                    onClick={() => {
                      setRenamingId(preset.id);
                      setRenameValue(preset.name);
                    }}
                  >
                    <LuPencil size="14" />
                  </IconButton>
                  <IconButton
                    aria-label="Archive preset"
                    size="xs"
                    variant="ghost"
                    onClick={() => archive(preset)}
                  >
                    <LuArchive size="14" />
                  </IconButton>
                </HStack>
              </HStack>
            )
          }
          description={preset.offer}
          selected={selectedId === preset.id}
          onSelect={() => onSelectionChange(preset)}
        />
      ))}
    </Stack>
  );
}
